/**
 * @hook useAuth
 * @description Reactive shell for the AuthOrchestrator logic, plus the
 * persona layer — which hat the signed-in person is currently wearing.
 *
 * ── Roles vs personas ────────────────────────────────────────────────
 *
 *   user.role          primary role. The security CEILING; only an admin
 *                      action changes it.
 *   user.assignedRoles every role this identity may act as.
 *   activePersona      which of the four experiences they are in right now
 *                      (customer / reviewer / staff). UI-layer,
 *                      localStorage-backed, never sent to a server.
 *   activeRole         the role string that persona resolves to, because the
 *                      navigation catalog and route guards filter on a role,
 *                      not on a persona.
 *
 * The persona layer exists because the previous switcher listed RAW ROLES and
 * only appeared when `availableRoles.length > 1`. A super admin holds exactly
 * one role, so the switcher was hidden from precisely the person who most
 * needed it: there was no way to see the platform as a customer without
 * logging out. Personas are DERIVED rather than stored — staff implies
 * customer, because an operator provisioning work on somebody's behalf is
 * doing customer things — so that account gets a real Customer ↔ Staff
 * choice without anyone editing a role.
 *
 * What this is NOT: impersonation. Every persona here is a hat on YOUR OWN
 * identity — same session, same token, same audit subject. Becoming a
 * different person is a separate, approval-gated flow, and the two must not
 * be confused. See useStaffPreview.js for the staff-only viewing tools.
 */
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { AuthOrchestrator } from './AuthOrchestrator';
import { logout as apiLogout } from '../api/auth';
import { identity } from '../api/identity';
import { SESSION_EXPIRED_EVENT } from '../api/client';
import { storage } from '../utils/storage';
import {
  personaOptionsOf,
  defaultPersonaOf,
  personaCapabilities,
  roleForPersona,
  rolesForPersona
} from '@moksy/common/src/permissions/personas.js';

const ACTIVE_ROLE_KEY = 'moksy_active_role';
const ACTIVE_PERSONA_KEY = 'moksy_active_persona';
// Which account the stored hat was chosen for.
//
// The hat is cleared on logout, so "the next person to sign in must not
// inherit the last one's persona" held — as long as the previous person
// pressed Log out. A session that expired, a closed tab, or a token cleared
// any other way left the hat behind, and the next sign-in silently inherited
// it: an admin logging in to their own console, landing as a member, and
// being told they do not have permission to open it.
const ACTIVE_PERSONA_UID_KEY = 'moksy_active_persona_uid';
// RF-075: whether the stored persona was CHOSEN (switchPersona/switchRole)
// rather than merely auto-settled. Every account holds the Customer persona
// ("every account holds the customer hat" — personas.js), so once anything
// ever wrote 'customer' here, the old settling effect below
// (`availablePersonas.includes(stored) ? stored : defaultPersonaOf(user)`)
// found it permanently valid and never reconsidered it — even for a staff
// account whose default should be STAFF. A support-desk account that landed
// on 'customer' on one early, possibly-transient render stayed there
// forever: every fresh sign-in, every reload, no self-service way out, and
// the console refused them as "signed in as a member" while their own
// profile said `role: "support", group: "owner"`.
// Without this, re-settling can only ever correct a persona the account no
// longer holds at all — never promote a more privileged one that was
// available the whole time.
const ACTIVE_PERSONA_EXPLICIT_KEY = 'moksy_active_persona_explicit';
const normalizeRole = (role) => String(role || '').trim().toLowerCase();

// RF-021: every call below used to go straight to `localStorage`. This hook
// runs on every authenticated page (useState's lazy initializer runs during
// the very first render), so a browser that blocks storage threw a
// SecurityError here before the app ever painted anything — and if the
// ErrorBoundary's own crash report then also touched storage unguarded (see
// utils/logger.js), that second throw happened inside componentDidCatch,
// which no boundary catches, taking the blank page from "a bug" to
// "unrecoverable". None of these three helpers can throw.
const safeGet = (key) => { try { return localStorage.getItem(key); } catch { return null; } };
const safeSet = (key, value) => { try { localStorage.setItem(key, value); } catch { /* storage blocked */ } };
const safeRemove = (key) => { try { localStorage.removeItem(key); } catch { /* storage blocked */ } };

/**
 * Fired after anything the signed-in person is entitled to has changed — a
 * plan bought, cancelled or resumed, a checkout confirmed, a pack credited.
 * Every mounted reader of plan data listens for it (useSubscriptions does), so
 * one purchase refreshes every screen rather than only the one that made it.
 */
export const ENTITLEMENTS_CHANGED_EVENT = 'moksy:entitlements-changed';

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const bootedRef = useRef(false);

  // Every persona this identity could switch into, INCLUDING the ones it may
  // not — a reviewer sees a disabled "Customer" carrying the reason, rather
  // than an absence they have to guess at.
  const personaOptions = useMemo(() => personaOptionsOf(user), [user?.role, user?.assignedRoles]);
  const availablePersonas = useMemo(
    () => personaOptions.filter((o) => o.available).map((o) => o.persona),
    [personaOptions]
  );

  const [activePersona, setActivePersonaState] = useState(
    () => safeGet(ACTIVE_PERSONA_KEY) || null
  );
  // activeRole is derived from localStorage, which React cannot observe.
  // Bumping this on every write is what makes a raw-role switch re-render;
  // without it the value changes and the screen does not.
  const [activeRoleTick, setActiveRoleTick] = useState(0);
  // 🎭 HAS THE HAT BEEN SETTLED FOR *THIS* ACCOUNT YET?
  //
  // `activePersona` and the stored role are read straight out of localStorage
  // at mount, so the first render can carry the PREVIOUS account's hat. The
  // effect below drops it — but a route guard rendering in that same commit
  // has already seen `activeRole: 'user'` and navigated away. Walked
  // 2026-09-09: a super admin opened the admin queues and was bounced to the
  // customer home page with `?reason=persona_restricted&as=user`, on an
  // account whose only role is super_admin. A reload then worked, which is the
  // signature of exactly this race.
  //
  // Guards wait for this rather than guessing.
  const [personaSettled, setPersonaSettled] = useState(false);

  /**
   * Roles this session may act as: the ones the account literally holds, plus
   * the ones its personas resolve to.
   *
   * The second half is load-bearing. A super admin holds only `super_admin`,
   * so without it the Customer persona resolves to `user`, the sidebar's
   * `availableRoles.includes(activeRole)` guard rejects it, and the switch
   * silently does nothing — the failure mode this whole change is fixing.
   *
   * Widening is safe because this list never leaves the browser: it drives
   * navigation, and the services re-derive authority from the token. It also
   * cannot widen anyone's real powers, since a persona is only offered when
   * the account already qualifies for it.
   */
  const availableRoles = useMemo(() => {
    if (!user) return [];
    const primary = normalizeRole(user.role) || 'user';
    const assigned = Array.isArray(user.assignedRoles) ? user.assignedRoles.map(normalizeRole) : [];
    const fromPersonas = personaOptions.filter((o) => o.available).map((o) => normalizeRole(o.role));
    return [primary, ...assigned, ...fromPersonas].filter((v, i, a) => v && a.indexOf(v) === i);
  }, [user?.role, user?.assignedRoles, personaOptions]);

  // Settle the persona once the user loads, and re-settle if their roles
  // change underneath a stored value — an ex-reviewer must not stay in a
  // reviewer hat their account no longer entitles them to.
  useEffect(() => {
    if (!user) return;

    // A hat belongs to the account that chose it. When the stored owner is
    // absent (set before this existed) or different (someone else signed in
    // on this browser), drop it and start from this account's own default.
    const uid = String(user.id || user.userId || user._id || '');
    const storedUid = String(safeGet(ACTIVE_PERSONA_UID_KEY) || '');
    if (uid && storedUid !== uid) {
      safeRemove(ACTIVE_PERSONA_KEY);
      safeRemove(ACTIVE_ROLE_KEY);
      safeRemove(ACTIVE_PERSONA_EXPLICIT_KEY);
      safeSet(ACTIVE_PERSONA_UID_KEY, uid);
    }

    // RF-075: a merely-held persona is not enough to keep — it must also
    // have been chosen (switchPersona/switchRole stamp the explicit flag).
    // An auto-settled value from an earlier, possibly less-informed render
    // is recomputed fresh every time, so a staff account can never get
    // stuck on the Customer hat every account holds by default.
    const stored = String(safeGet(ACTIVE_PERSONA_KEY) || '').toLowerCase();
    const wasChosen = safeGet(ACTIVE_PERSONA_EXPLICIT_KEY) === '1';
    const next = (wasChosen && availablePersonas.includes(stored)) ? stored : defaultPersonaOf(user);
    safeSet(ACTIVE_PERSONA_KEY, next);

    // Only rewrite the role when the stored one does not belong to this hat.
    // Overwriting unconditionally would erase a sub-role choice (customer as
    // `designer` rather than `user`) on every page load, so the setting would
    // appear to work and then revert.
    const storedRole = normalizeRole(safeGet(ACTIVE_ROLE_KEY));
    if (!rolesForPersona(user, next).includes(storedRole)) {
      safeSet(ACTIVE_ROLE_KEY, roleForPersona(user, next) || normalizeRole(user.role) || 'user');
    }
    setActivePersonaState(next);
    setActiveRoleTick((n) => n + 1);
    setPersonaSettled(true);
  }, [user?.role, availablePersonas.join(',')]);

  // Signing out (or a session ending) leaves nothing settled for the next
  // person: the flag follows the identity, like the hat itself.
  useEffect(() => { if (!user) setPersonaSettled(false); }, [user]);

  const safeActivePersona = useMemo(() => {
    const p = String(activePersona || '').toLowerCase();
    return availablePersonas.includes(p) ? p : (user ? defaultPersonaOf(user) : null);
  }, [activePersona, availablePersonas, user?.role]);

  /** Every role the current hat contains — usually one, occasionally several. */
  const rolesInPersona = useMemo(
    () => rolesForPersona(user, safeActivePersona),
    [user?.role, user?.assignedRoles, safeActivePersona]
  );

  /**
   * The role the current hat resolves to. Everything that filters navigation
   * or guards a route reads this, not the persona.
   *
   * A stored role wins when it belongs to the active persona. That preserves
   * the one thing the old raw-role switcher did that personas alone cannot:
   * an account holding both `user` and `designer` sees different navigation
   * under each, and both live inside the customer persona. Without this the
   * choice would silently collapse to whichever role came first.
   */
  const activeRole = useMemo(() => {
    if (!user) return null;
    const stored = normalizeRole(safeGet(ACTIVE_ROLE_KEY));
    if (stored && rolesInPersona.includes(stored)) return stored;
    return roleForPersona(user, safeActivePersona) || normalizeRole(user.role) || 'user';
  }, [user?.role, user?.assignedRoles, safeActivePersona, rolesInPersona, activeRoleTick]);

  /** What the current hat may do, as facts — used to warn before a refusal. */
  const personaLimits = useMemo(
    () => personaCapabilities(user, safeActivePersona),
    [user?.role, user?.assignedRoles, safeActivePersona]
  );

  /**
   * 🚪 EFFECT: The session died underneath us.
   *
   * The API client clears storage on an unrecoverable 401, but storage is
   * not what ProtectedRoute reads — this hook's `user` is. Without this
   * listener the two disagreed: tokens gone, React still believing it was
   * signed in, so every navigation rendered a protected page, fired a
   * request, got another 401 and redirected again. That flicker was the
   * only outward sign, and a hard refresh was the only way to find out
   * the session had expired.
   *
   * `terminateSession` is the same teardown the logout button uses, so an
   * expiry and a deliberate sign-out leave the app in one state, not two.
   */
  useEffect(() => {
    const onExpired = () => { AuthOrchestrator.terminateSession(setUser); };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  /**
   * 🔄 EFFECT: Boot Sequence
   */
  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    AuthOrchestrator.hydrateSession(setUser, setLoading);
  }, []);

  /**
   * 🎭 ACTION: Put on a different hat.
   *
   * UI-layer only — no server round-trip, no new token. Refuses personas this
   * identity does not hold rather than falling back to a default, because a
   * switcher that quietly lands you somewhere else is worse than one that
   * does nothing.
   */
  const switchPersona = useCallback((persona) => {
    const next = String(persona || '').toLowerCase();
    if (!availablePersonas.includes(next)) return false;
    const role = roleForPersona(user, next);
    if (!role) return false;
    safeSet(ACTIVE_PERSONA_KEY, next);
    safeSet(ACTIVE_ROLE_KEY, role);
    // A real choice, not a guess the settling effect made — see
    // ACTIVE_PERSONA_EXPLICIT_KEY's comment above (RF-075).
    safeSet(ACTIVE_PERSONA_EXPLICIT_KEY, '1');
    // Stamp the owner so this choice survives a reload but not a different
    // account signing in on the same browser.
    safeSet(ACTIVE_PERSONA_UID_KEY, String(user?.id || user?.userId || user?._id || ''));
    setActivePersonaState(next);
    setActiveRoleTick((n) => n + 1);
    return true;
  }, [availablePersonas, user?.role, user?.assignedRoles]);

  /**
   * 🔄 ACTION: Switch by raw role.
   *
   * Retained for callers that still think in roles. Setting a role also moves
   * the persona to whichever one that role belongs to, so the two can never
   * disagree about which hat is on.
   */
  const switchRole = useCallback((role) => {
    const normalizedRole = normalizeRole(role);
    if (!availableRoles.includes(normalizedRole)) return;
    const owning = personaOptions.find((o) => o.available && o.role === normalizedRole);
    safeSet(ACTIVE_ROLE_KEY, normalizedRole);
    // A real choice, not a guess the settling effect made (RF-075).
    safeSet(ACTIVE_PERSONA_EXPLICIT_KEY, '1');
    if (owning) {
      safeSet(ACTIVE_PERSONA_KEY, owning.persona);
      setActivePersonaState(owning.persona);
    }
    setActiveRoleTick((n) => n + 1);
  }, [availableRoles, personaOptions]);

  /**
   * 🚪 ACTION: Logout
   * Clears the hat as well as the session — the next person to sign in on
   * this browser must not inherit the last one's persona.
   */
  const logout = useCallback(async () => {
    safeRemove(ACTIVE_ROLE_KEY);
    safeRemove(ACTIVE_PERSONA_KEY);
    safeRemove(ACTIVE_PERSONA_UID_KEY);
    safeRemove(ACTIVE_PERSONA_EXPLICIT_KEY);
    // A cookie decision is this person's, not this browser's (GC-078). Left
    // behind, it became the next person at the keyboard's recorded consent to
    // marketing — someone who had never seen the banner. The banner reads the
    // subject stamped on the record and would refuse it anyway; clearing it
    // here means the stranger is also not shown somebody else's answers.
    safeRemove(storage.KEYS.COOKIE_CONSENT);
    // Best-effort: revoke the refresh token on the server before clearing local state.
    // Failure (e.g. already expired or network error) is silently ignored — the local
    // session is terminated regardless.
    try { await apiLogout(); } catch { /* best effort */ }
    AuthOrchestrator.terminateSession(setUser);
  }, []);

  // Returns the promise. It used to return nothing, so every
  // `await refreshAuth()` in the app carried straight on before the profile
  // had been re-read.
  const refreshAuth = useCallback(
    () => AuthOrchestrator.hydrateSession(setUser, setLoading),
    []
  );

  /**
   * 🔓 ACTION: Something this person is entitled to just changed.
   *
   * Every gate — FeatureGate, RequirePaidTier, the upgrade prompts — reads
   * `user.subscription`, which is attached to the profile once, at sign-in.
   * Nothing re-read it after a purchase, so a member who had just seen
   * "Payment complete" was still told to upgrade on every locked screen until
   * they reloaded the page.
   *
   * Call this after ANY entitlement change. It re-reads the profile (and with
   * it the plan summary), then broadcasts ENTITLEMENTS_CHANGED_EVENT so other
   * plan readers on screen refresh too.
   *
   * Deliberately not `refreshAuth`: that path treats a failed read as a dead
   * session and signs the person out. A blip while re-reading a plan must not
   * log out somebody who has just paid — the session they had stays.
   *
   * `source` lets the caller that already refreshed its own data skip the
   * broadcast echo.
   */
  const refreshEntitlements = useCallback(async ({ source = null } = {}) => {
    try {
      if (storage.hasUsableAccessToken()) {
        const profile = await identity.fetchProfile();
        if (profile) {
          setUser((prev) => (prev
            ? { ...prev, ...profile, id: prev.id || profile.id, userId: prev.userId || profile.userId || profile.id }
            : prev));
        }
      }
    } catch {
      /* keep the session — see above */
    } finally {
      window.dispatchEvent(new CustomEvent(ENTITLEMENTS_CHANGED_EVENT, { detail: { source } }));
    }
  }, []);

  return {
    user,
    loading,
    logout,
    isAuthenticated: !!user,
    // ── Persona layer ──
    activePersona: safeActivePersona,
    availablePersonas,
    personaOptions,
    personaLimits,
    switchPersona,
    // ── Role layer (what navigation and guards read) ──
    activeRole,
    // False until the stored hat has been reconciled with THIS account.
    personaSettled,
    availableRoles,
    rolesInPersona,
    switchRole,
    // Logic: Expose sync capability to components
    refreshAuth,
    // After any purchase, cancellation or credit — see above.
    refreshEntitlements
  };
};
